/**
 * Conferência das metas do admin — o que o `recalcularMetas()` do protótipo faz
 * no DOM, aqui como função pura (é esta que o teste cobre).
 *
 * A regra que manda: a soma das divisões divergir da meta geral **informa, não
 * bloqueia** (P-10 — hoje R$ 110,3 mi de soma contra R$ 100 mi de meta geral).
 * Na prática a diretoria trabalha com a soma das partes diferente do topo, e um
 * painel que recusa salvar por causa disso trava o cadastro na véspera.
 *
 * Só o inválido é recusado: meta negativa e meta com texto.
 */

/** Tolerância de 1 real: diferença de centavo é arredondamento, não divergência. */
const TOLERANCIA_REAIS = 1

export type MetaLida =
  | { ok: true; valor: number }
  | { ok: false; motivo: 'vazio' | 'texto' | 'negativa' }

/**
 * Lê o que está no campo. `vazio` é diferente de inválido: campo em branco é
 * "não mexer nesta meta" e simplesmente não vai no PUT — o cadastro não tem
 * como apagar meta.
 */
export function lerMeta(bruto: string | number | null | undefined): MetaLida {
  if (bruto === null || bruto === undefined) return { ok: false, motivo: 'vazio' }
  const texto = String(bruto).trim()
  if (texto === '') return { ok: false, motivo: 'vazio' }
  /* `Number` em vez de `parseFloat`: "6.500.000" e "6 milhões" precisam virar
     NaN, e o `parseFloat` devolveria 6 para os dois. */
  const valor = Number(texto)
  if (!Number.isFinite(valor)) return { ok: false, motivo: 'texto' }
  if (valor < 0) return { ok: false, motivo: 'negativa' }
  return { ok: true, valor }
}

export type SubitemMeta = {
  codigo: string
  rotulo: string
  meta: string
  /** Marcado na aba Marcas e ainda não gravado: o servidor responderia
   *  `subitem_nao_exibido` e derrubaria o PUT inteiro, que é tudo-ou-nada. */
  novo?: boolean
}

export type LinhaMeta = {
  /** Identidade da linha na tela. Numa marca nova o `id` ainda está vazio, e
   *  duas marcas novas colidiriam no mapa da conferência. */
  chave: string
  id: string
  nome: string
  /** Divisão desativada não está no telão, então fica fora do resumo. */
  ativa: boolean
  /** Marca acrescentada na tela e ainda não gravada. */
  nova?: boolean
  meta: string
  subitens: SubitemMeta[]
}

export type ConferenciaDivisao = {
  chave: string
  id: string
  /** `null` = campo vazio ou inválido; zero é um número. */
  meta: number | null
  /** `null` = divisão sem subitem (a coluna mostra "—"). */
  somaSubitens: number | null
  /** A soma dos subitens difere da meta da divisão: borda amarela, não trava. */
  divergente: boolean
}

export type ProblemaMeta = {
  onde: string
  motivo: 'texto' | 'negativa'
  mensagem: string
}

export type Conferencia = {
  metaGeral: number | null
  somaDivisoes: number
  qtdDivisoes: number
  /** Quantas das ativas têm meta lida. Zero = ninguém cadastrou nada, e aí a
   *  soma é "—", não "R$ 0" — zero por falha é a pergunta na sala. */
  qtdComMeta: number
  /** `soma - geral`, ou `null` quando a meta geral ainda não foi cadastrada. */
  diferenca: number | null
  /** Chaveado pela `chave` da linha, não pelo id da divisão. */
  porDivisao: Record<string, ConferenciaDivisao>
  problemas: ProblemaMeta[]
}

function problema(onde: string, motivo: 'texto' | 'negativa', bruto: string): ProblemaMeta {
  /* "A meta geral" em vez de "Meta de meta geral": a mensagem vai para a tela. */
  const sujeito = onde === 'meta geral' ? 'A meta geral' : `A meta de ${onde}`
  const mensagem = motivo === 'negativa'
    ? `${sujeito} não pode ser negativa: ${bruto.trim()}.`
    : `${sujeito} precisa ser um número em reais: "${bruto.trim()}".`
  return { onde, motivo, mensagem }
}

export function conferirMetas(metaGeralBruta: string, linhas: LinhaMeta[]): Conferencia {
  const problemas: ProblemaMeta[] = []
  const porDivisao: Record<string, ConferenciaDivisao> = {}

  const geral = lerMeta(metaGeralBruta)
  if (!geral.ok && geral.motivo !== 'vazio') {
    problemas.push(problema('meta geral', geral.motivo, metaGeralBruta))
  }

  let somaDivisoes = 0
  let qtdDivisoes = 0
  let qtdComMeta = 0

  for (const linha of linhas) {
    const meta = lerMeta(linha.meta)
    if (!meta.ok && meta.motivo !== 'vazio') {
      problemas.push(problema(linha.nome, meta.motivo, linha.meta))
    }

    let somaSubitens: number | null = null
    for (const subitem of linha.subitens) {
      const valor = lerMeta(subitem.meta)
      if (!valor.ok) {
        if (valor.motivo !== 'vazio') {
          problemas.push(problema(`${linha.nome} / ${subitem.rotulo}`, valor.motivo, subitem.meta))
        }
        continue
      }
      somaSubitens = (somaSubitens ?? 0) + valor.valor
    }
    /* Divisão com subitem mas nenhum valor lido ainda conta como zero, e não
       como "sem subitem": a coluna precisa mostrar a soma, inclusive quando ela
       é zero e divergente da meta da divisão. */
    if (somaSubitens === null && linha.subitens.length > 0) somaSubitens = 0

    const valorMeta = meta.ok ? meta.valor : null
    porDivisao[linha.chave] = {
      chave: linha.chave,
      id: linha.id,
      meta: valorMeta,
      somaSubitens,
      divergente: somaSubitens !== null && valorMeta !== null
        && Math.abs(somaSubitens - valorMeta) >= TOLERANCIA_REAIS,
    }

    /* Só divisão ativa entra no resumo: a diferença existe para ser comparada
       com o que o telão mostra, e divisão desativada não está no telão. É a
       mesma conta do `resumoMetas()` do cadastro. */
    if (linha.ativa) {
      qtdDivisoes += 1
      if (valorMeta !== null) {
        somaDivisoes += valorMeta
        qtdComMeta += 1
      }
    }
  }

  const metaGeral = geral.ok ? geral.valor : null
  return {
    metaGeral,
    somaDivisoes,
    qtdDivisoes,
    qtdComMeta,
    diferenca: metaGeral === null ? null : somaDivisoes - metaGeral,
    porDivisao,
    problemas,
  }
}

export type MetaEnviada = {
  divisaoId: string
  valor?: number
  subitens?: Array<{ codigo: string; valor: number }>
}

export type EnvioMetas =
  | { ok: true; metaGeral: number; metas: MetaEnviada[]; avisos: string[] }
  | { ok: false; problemas: string[] }

/**
 * Monta o corpo do `PUT /api/admin/metas`.
 *
 * Campo vazio é OMITIDO, porque ausente é "não mexer nesta meta" no servidor;
 * mandar `null` é recusado de propósito lá, e seria defeito desta tela.
 */
export function montarEnvioMetas(metaGeralBruta: string, linhas: LinhaMeta[]): EnvioMetas {
  const conferencia = conferirMetas(metaGeralBruta, linhas)
  const problemas = conferencia.problemas.map(p => p.mensagem)

  const geral = lerMeta(metaGeralBruta)
  if (!geral.ok) {
    /* A meta geral é o único campo obrigatório: `api.admin.salvarMetas` a leva
       sempre, e vazio viraria `null`, que o servidor recusa. */
    if (geral.motivo === 'vazio') problemas.push('Informe a meta geral de 2026, em reais.')
    return { ok: false, problemas }
  }
  if (problemas.length > 0) return { ok: false, problemas }

  const avisos: string[] = []
  const metas: MetaEnviada[] = []
  /* Campo em branco é "não mexer", e silêncio aqui é o pior defeito desta aba:
     `<input type=number>` devolve "" para qualquer conteúdo que o navegador não
     aceite como número — quem digita "6.500.000" ou "6,5", do jeito que se
     escreve em português, vê o campo esvaziar e lê "Metas salvas." sem que
     aquela meta tenha sido salva. */
  const emBranco: string[] = []
  const naoGravados: string[] = []

  for (const linha of linhas) {
    if (linha.nova) {
      /* Meta de marca que o servidor ainda não conhece volta como
         `divisao_inexistente`. Avisa e segue: o resto das metas salva. */
      avisos.push(`A meta de "${linha.nome}" só entra depois de salvar a aba Marcas.`)
      continue
    }
    const meta = lerMeta(linha.meta)
    if (!meta.ok && meta.motivo === 'vazio') emBranco.push(linha.nome || linha.id)

    const subitens: Array<{ codigo: string; valor: number }> = []
    for (const subitem of linha.subitens) {
      const lido = lerMeta(subitem.meta)
      if (!lido.ok) continue
      /* Subitem marcado na aba Marcas e ainda não gravado faria o servidor
         responder `subitem_nao_exibido` — e o PUT é tudo-ou-nada, então UMA
         caixinha marcada derrubaria as metas das oito marcas. */
      if (subitem.novo) {
        naoGravados.push(`${linha.nome || linha.id} / ${subitem.rotulo}`)
        continue
      }
      subitens.push({ codigo: subitem.codigo, valor: lido.valor })
    }

    if (!meta.ok && subitens.length === 0) continue
    const alvo: MetaEnviada = { divisaoId: linha.id }
    if (meta.ok) alvo.valor = meta.valor
    if (subitens.length > 0) alvo.subitens = subitens
    metas.push(alvo)
  }

  if (naoGravados.length > 0) {
    avisos.push(
      `Subitem ainda não gravado: ${naoGravados.join(', ')}. `
      + 'Salve a aba Marcas e depois as metas dele.',
    )
  }
  if (emBranco.length > 0) {
    avisos.push(`Meta em branco, NÃO alterada: ${emBranco.join(', ')}.`)
  }

  return { ok: true, metaGeral: geral.valor, metas, avisos }
}
